import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { ChannelBadge } from "@/components/ChannelBadge";
import { toast } from "sonner";
import { Plus, Trash, PaperPlaneTilt, Clock, X, PlayCircle, PauseCircle, UsersThree } from "@phosphor-icons/react";
import { cadenceStatusClass } from "@/lib/ui-helpers";

const TRIGGERS = [
  { v: "manual", l: "Manual enroll" },
  { v: "new_follower", l: "New follower (Instagram)" },
  { v: "post_like", l: "Liked a post (Instagram)" },
  { v: "post_comment", l: "Commented on post (Instagram)" },
  { v: "story_mention", l: "Story mention (Instagram)" },
  { v: "story_reply", l: "Story reply (Instagram)" },
  { v: "message_received", l: "Inbound message" },
];

function StepEditor({ steps, setSteps }) {
  const add = (type) => setSteps([...steps, {
    id: crypto.randomUUID(),
    type,
    template_id: null,
    message_body: type === "send_message" ? "" : null,
    wait_hours: type === "wait" ? 24 : null,
    order: steps.length,
  }]);
  const update = (idx, patch) => setSteps(steps.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const remove = (idx) => setSteps(steps.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      {steps.length === 0 && (
        <div className="text-center py-8 border border-dashed border-white/10 text-white/40 text-sm font-mono">
          no steps — add one below
        </div>
      )}
      {steps.map((s, i) => (
        <div key={s.id} className="relative">
          {i > 0 && <div className="absolute -top-3 left-6 w-px h-3 bg-white/20" />}
          <div className="border border-white/10 bg-[#121212] p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center shrink-0">
              {s.type === "send_message" ? <PaperPlaneTilt size={16} /> : <Clock size={16} />}
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/40 mb-2">
                Step {i + 1} · {s.type === "send_message" ? "Send message" : "Wait"}
              </div>
              {s.type === "send_message" ? (
                <textarea
                  data-testid={`cadence-step-body-${i}`}
                  value={s.message_body || ""}
                  onChange={(e) => update(i, { message_body: e.target.value })}
                  rows={2}
                  placeholder="Message body — supports {{name}}"
                  className="w-full bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2 outline-none text-sm transition-colors font-mono"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    data-testid={`cadence-step-wait-${i}`}
                    type="number" min="0" step="0.5"
                    value={s.wait_hours || 0}
                    onChange={(e) => update(i, { wait_hours: parseFloat(e.target.value) })}
                    className="w-24 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2 outline-none text-sm transition-colors font-mono"
                  />
                  <span className="text-sm text-white/60">hours before next step</span>
                </div>
              )}
            </div>
            <button type="button" onClick={() => remove(i)} data-testid={`cadence-step-remove-${i}`}
              className="p-1.5 hover:bg-white/10 rounded-sm text-white/40 hover:text-[#E1306C] transition-colors">
              <Trash size={14} />
            </button>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <button type="button" data-testid="cadence-add-message" onClick={() => add("send_message")}
          className="flex-1 border border-dashed border-white/15 hover:border-white/40 py-2.5 rounded-sm text-sm flex items-center justify-center gap-2 transition-colors">
          <PaperPlaneTilt size={14} /> Add message
        </button>
        <button type="button" data-testid="cadence-add-wait" onClick={() => add("wait")}
          className="flex-1 border border-dashed border-white/15 hover:border-white/40 py-2.5 rounded-sm text-sm flex items-center justify-center gap-2 transition-colors">
          <Clock size={14} /> Add wait
        </button>
      </div>
    </div>
  );
}

export default function CadencesPage() {
  const [cadences, setCadences] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [enrollFor, setEnrollFor] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [form, setForm] = useState({ name: "", channel: "whatsapp", trigger: "manual", steps: [], status: "draft" });

  const load = useCallback(async () => {
    const [{ data: c }, { data: p }] = await Promise.all([api.get("/cadences"), api.get("/contacts")]);
    setCadences(c);
    setContacts(p);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cadences", form);
      toast.success("Cadence created");
      setForm({ name: "", channel: "whatsapp", trigger: "manual", steps: [], status: "draft" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (c) => {
    const newStatus = c.status === "active" ? "paused" : "active";
    await api.patch(`/cadences/${c.id}`, { status: newStatus });
    toast.success(`Cadence ${newStatus}`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete cadence and all enrollments?")) return;
    await api.delete(`/cadences/${id}`);
    toast.success("Deleted");
    load();
  };

  const enroll = async () => {
    const ids = Object.keys(selectedContacts).filter((k) => selectedContacts[k]);
    if (ids.length === 0) return toast.error("Select at least one contact");
    await api.post(`/cadences/${enrollFor.id}/enroll`, { contact_ids: ids });
    toast.success(`Enrolled ${ids.length} contact(s)`);
    setEnrollFor(null);
    setSelectedContacts({});
  };

  return (
    <Layout>
      <div className="border-b border-white/10 px-10 py-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// automations</div>
          <h1 className="font-heading text-5xl tracking-tight mt-2">Cadences</h1>
        </div>
        <button data-testid="cadence-new-button" onClick={() => setShowForm(true)}
          className="bg-white text-black px-4 py-2.5 rounded-sm flex items-center gap-2 hover:bg-white/90 transition-colors">
          <Plus size={16} weight="bold" /> New cadence
        </button>
      </div>

      <div className="p-10 space-y-4">
        {cadences.length === 0 && (
          <div className="text-center py-20 text-white/40 font-mono text-sm border border-dashed border-white/10">
            no cadences yet
          </div>
        )}
        {cadences.map((c) => (
          <div key={c.id} data-testid={`cadence-card-${c.id}`} className="border border-white/10 bg-[#121212] p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <div className="font-heading text-2xl tracking-tight">{c.name}</div>
                  <ChannelBadge channel={c.channel} size="xs" />
                  <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-sm border ${cadenceStatusClass(c.status)}`}>{c.status}</span>
                </div>
                <div className="text-sm text-white/50 font-mono mt-1">
                  trigger: {TRIGGERS.find((t) => t.v === c.trigger)?.l || c.trigger} · {c.steps?.length || 0} step(s)
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`cadence-enroll-${c.id}`} onClick={() => setEnrollFor(c)}
                  className="p-2 hover:bg-white/10 rounded-sm text-white/60 hover:text-white transition-colors" title="Enroll contacts">
                  <UsersThree size={16} />
                </button>
                <button data-testid={`cadence-toggle-${c.id}`} onClick={() => toggle(c)}
                  className="p-2 hover:bg-white/10 rounded-sm text-white/60 hover:text-white transition-colors">
                  {c.status === "active" ? <PauseCircle size={16} /> : <PlayCircle size={16} />}
                </button>
                <button data-testid={`cadence-delete-${c.id}`} onClick={() => remove(c.id)}
                  className="p-2 hover:bg-white/10 rounded-sm text-white/40 hover:text-[#E1306C] transition-colors">
                  <Trash size={14} />
                </button>
              </div>
            </div>

            {c.steps?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-white/5 flex flex-wrap gap-2">
                {c.steps.map((s, i) => (
                  <div key={s.id || i} className="flex items-center gap-1 text-xs font-mono border border-white/10 px-2 py-1 rounded-sm">
                    <span className="text-white/40">{i + 1}.</span>
                    {s.type === "wait" ? (
                      <><Clock size={12} /> wait {s.wait_hours}h</>
                    ) : (
                      <><PaperPlaneTilt size={12} /> send msg</>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// new</div>
                <div className="font-heading text-2xl tracking-tight">New cadence</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-white/10 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Name</label>
                  <input data-testid="cadence-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required
                    className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Channel</label>
                  <select data-testid="cadence-form-channel" value={form.channel} onChange={(e) => setForm({...form, channel: e.target.value})}
                    className="w-full mt-1 bg-[#0A0A0A] border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors">
                    <option value="whatsapp">WhatsApp</option>
                    <option value="instagram">Instagram</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Trigger</label>
                <select data-testid="cadence-form-trigger" value={form.trigger} onChange={(e) => setForm({...form, trigger: e.target.value})}
                  className="w-full mt-1 bg-[#0A0A0A] border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors">
                  {TRIGGERS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Steps</label>
                <div className="mt-2">
                  <StepEditor steps={form.steps} setSteps={(steps) => setForm({...form, steps})} />
                </div>
              </div>
              <button data-testid="cadence-form-submit" type="submit"
                className="w-full bg-white text-black py-2.5 rounded-sm font-medium hover:bg-white/90 transition-colors">
                Create cadence
              </button>
            </form>
          </div>
        </div>
      )}

      {enrollFor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// enroll into {enrollFor.name}</div>
                <div className="font-heading text-2xl tracking-tight">Select contacts</div>
              </div>
              <button onClick={() => { setEnrollFor(null); setSelectedContacts({}); }} className="p-2 hover:bg-white/10 rounded-sm">
                <X size={16} />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {contacts.filter((c) => c.channel === enrollFor.channel).length === 0 && (
                <div className="p-8 text-center text-white/40 font-mono text-sm">
                  no {enrollFor.channel} contacts yet
                </div>
              )}
              {contacts.filter((c) => c.channel === enrollFor.channel).map((c) => (
                <label key={c.id} data-testid={`enroll-contact-${c.id}`}
                  className="flex items-center gap-3 px-6 py-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer">
                  <input type="checkbox" checked={!!selectedContacts[c.id]}
                    onChange={(e) => setSelectedContacts({...selectedContacts, [c.id]: e.target.checked})}
                    className="accent-white" />
                  <div className="flex-1">
                    <div className="text-sm">{c.name}</div>
                    <div className="text-xs text-white/40 font-mono">{c.handle || c.external_id}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="p-4 border-t border-white/10">
              <button data-testid="enroll-submit" onClick={enroll}
                className="w-full bg-white text-black py-2.5 rounded-sm font-medium hover:bg-white/90 transition-colors">
                Enroll selected
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
